import React, { useEffect, useMemo, useState } from 'react';
import { Table, ListGroup, Button, Badge, Row, Col, Form, Offcanvas, ButtonGroup } from 'react-bootstrap';
import { useReactTable, getCoreRowModel, flexRender, getPaginationRowModel, getSortedRowModel } from '@tanstack/react-table';
import { toast } from 'react-toastify';
import { displayDate, displayOutDate } from '../../utils/date';
import { parseMoney, formatMoney } from '../../utils/money';
import { getVisiblePages } from '../../utils/pagination';
import { confirmToast } from '../../components/Configs/ConfirmToast';
import ItemDetailsModal from '../../components/Items/ItemDetailsModal';
import ItemLogsModal from '../../components/Items/ItemLogsModal';
import ProcessModal from '../../components/Items/ProcessModal';
import AddItemModal from '../../components/Items/AddItemModal';
import EditItemModal from '../../components/Items/EditItemModal';
import OutputModal from '../../components/Items/OutputModal';
import UploadFilesModal from '../../components/Configs/UploadFilesModal';
import { getCategories } from '../../services/Configs/ItemCategoriesService';
import { getLocations } from '../../services/Configs/LocationsService';
import { getStatuses } from '../../services/Other/GeneralQueries';
import { getInventory, getLastReversibleMovement, undoMovement, getItemFolioRanges } from '../../services/Items/Inventory';
import { getEvidenceSummaryByItems } from '../../services/Items/UploadFiles';

function stageBadge(stage) {
    const map = {
        raw: { text: 'Materia prima', bg: 'primary' },
        processing: { text: 'Proceso', bg: 'warning' },
        finished: { text: 'Terminado', bg: 'secondary' },
    };
    const s = map[stage] || { text: stage || 'N/D', bg: 'dark' };
    return <Badge bg={s.bg}>{s.text}</Badge>;
}

function categoryBadge(name) {
    const n = (name || '').toLowerCase();
    const bg =
        n.includes('mater') ? 'primary' :
            n.includes('proces') ? 'warning' :
                n.includes('termin') ? 'success' :
                    'secondary';
    return <Badge bg={bg}>{name || 'N/D'}</Badge>;
}

function statusBadge(name) {
    const n = (name || '').toLowerCase();

    const bg =
        n.includes('vend') ? 'success' :
            n.includes('cortes') ? 'info' :
                n.includes('merma') ? 'danger' :
                    n.includes('promo') ? 'warning' :
                        n.includes('degusta') ? 'secondary' :
                            n.includes('agot') ? 'dark' :
                                n.includes('dispon') ? 'primary' :
                                    n.includes('proce') ? 'warning' :
                                        'dark';

    return <Badge bg={bg}>{name || 'N/D'}</Badge>;
}

function Inventory() {
    const [categories, setCategories] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [hideZero, setHideZero] = useState(true);
    const [selectedId, setSelectedId] = useState(null);
    const [showDetails, setShowDetails] = useState(false);
    const [showLogs, setShowLogs] = useState(false);
    const [showProcess, setShowProcess] = useState(false);
    const [processMeta, setProcessMeta] = useState({ itemId: null, stage: null, forceProcessingForm: false });
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedLocations, setSelectedLocations] = useState([]);
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [show, setShow] = useState(false);
    const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 10 });
    const [sorting, setSorting] = useState([{ id: 'id', desc: false }]); // ASC por id
    const [columnVisibility, setColumnVisibility] = useState({ id: true }); // id oculta
    const [filtering, setFiltering] = useState('');
    const [stages, setStages] = useState([
        { id: 'raw', name: 'Materia prima' },
        { id: 'processing', name: 'Proceso' },
        { id: 'finished', name: 'Terminado' },
    ]);
    const [selectedStages, setSelectedStages] = useState(['raw', 'processing', 'finished']);
    const [qtyMin, setQtyMin] = useState('');
    const [qtyMax, setQtyMax] = useState('');
    const [detailsReadOnly, setDetailsReadOnly] = useState(false);
    const [showSale, setShowSale] = useState(false);
    const [stageFilter, setStageFilter] = useState('all');
    const [showAdd, setShowAdd] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [folioMap, setFolioMap] = useState({});

    const [dateField, setDateField] = useState('acquisitionDate'); // acquisitionDate | outDate
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const [searchInput, setSearchInput] = useState('');
    const [searchText, setSearchText] = useState('');

    const [showOutput, setShowOutput] = useState(false);

    const [evidenceMap, setEvidenceMap] = useState({});
    const [showEvidence, setShowEvidence] = useState(false);
    const [selectedItemName, setSelectedItemName] = useState('');

    const openOutputFromDetails = (itemId) => {
        setSelectedId(itemId);
        setShowDetails(false);
        setShowOutput(true);
    };

    const openEvidenceFromDetails = (itemId, itemName) => {
        setSelectedId(itemId);
        setSelectedItemName(itemName || '');
        setShowDetails(false);
        setShowEvidence(true);
    };

    function evidenceBadge(info) {
        if (!info?.hasEvidence) return <Badge bg="secondary">Sin evidencia</Badge>;
        if (info.evidenceType === 'image') return <Badge bg="info">Imagen</Badge>;
        if (info.evidenceType === 'document') return <Badge bg="dark">Documento</Badge>;
        if (info.evidenceType === 'mixed') return <Badge bg="success">Mixta</Badge>;
        return <Badge bg="secondary">Sin evidencia</Badge>;
    }

    const handleStageFilter = (e) => {
        const v = e.target.value;
        setStageFilter(v);

        if (v === 'all') setSelectedStages(stages.map((s) => s.id));
        else setSelectedStages([v]);
    };

    const openSaleFromDetails = (itemId) => {
        setSelectedId(itemId);
        setShowDetails(false);
        setShowSale(true);
    };

    const [showWaste, setShowWaste] = useState(false);
    const openWasteFromDetails = (itemId) => {
        setSelectedId(itemId);
        setShowDetails(false);
        setShowWaste(true);
    };

    const [showCourtesy, setShowCourtesy] = useState(false);
    const openCourtesyFromDetails = (itemId) => {
        setSelectedId(itemId);
        setShowDetails(false);
        setShowCourtesy(true);
    };

    const [detailsRefresh, setDetailsRefresh] = useState(0);

    const formatFolio = (r) => {
        const count = Number(r.count ?? 0);
        const isSingle = count <= 1 || r.startFolioName === r.endFolioName;
        return isSingle
            ? r.startFolioName
            : `${r.startFolioName} – ${r.endFolioName} (${count})`;
    };

    const formatFoliosForCell = (ranges) =>
        ranges.map(formatFolio).join(' | ');

    const handleClose = () => setShow(false);
    const handleShow = () => setShow(true);

    const handleShowDetails = (row) => {
        setSelectedId(row.id);
        setDetailsReadOnly(false);
        setShowDetails(true);
    };

    const handleOpenModalLogs = (id) => { setSelectedId(id); setShowDetails(false); setShowLogs(true); };

    const openProcessFromDetails = ({ itemId, currentStage }) => {
        setShowDetails(false);
        setProcessMeta({
            itemId,
            stage: currentStage,
            forceProcessingForm: currentStage === 'raw'
        });
        setShowProcess(true);
    };

    const toggleStage = (id) =>
        setSelectedStages(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const handleCheckboxChangeCategories = (id) => {
        setSelectedCategories((prevSelectedIds) =>
            prevSelectedIds.includes(id)
                ? prevSelectedIds.filter((selectedId) => selectedId !== id)
                : [...prevSelectedIds, id]
        );
    };

    const handleCheckboxChangeLocations = (id) => {
        setSelectedLocations((prevSelectedIds) =>
            prevSelectedIds.includes(id)
                ? prevSelectedIds.filter((selectedId) => selectedId !== id)
                : [...prevSelectedIds, id]
        );
    };

    const handleCheckboxChangeStatuses = (id) => {
        setSelectedStatuses((prevSelectedIds) =>
            prevSelectedIds.includes(id)
                ? prevSelectedIds.filter((selectedId) => selectedId !== id)
                : [...prevSelectedIds, id]
        );
    };

    const columns = [
        {
            header: 'Acciones',
            accessorKey: 'id',
            cell: ({ row }) => {
                const isFinished = row.original.stage === 'finished';
                return (
                    <ButtonGroup size="sm">
                        <div className='actions-cell'>
                            <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handleShowDetails(row.original)}
                                title={isFinished ? 'Proceso terminado' : 'Ver detalles'}
                            >
                                Detalles
                            </Button>
                            <Button
                                variant="outline-danger"
                                size="sm"
                                onClick={() => handleUndo(row.original.id)}
                                disabled={false}
                            >
                                Undo
                            </Button>
                        </div>
                    </ButtonGroup>
                );
            }
        },
        { header: 'Nombre', accessorKey: 'name' },
        { header: 'Descripción', accessorKey: 'description' },
        {
            header: 'Categoría',
            accessorKey: 'categoryName',
            cell: ({ row }) => categoryBadge(row.original.categoryName)
        },
        {
            header: 'Cantidad en inventario', accessorKey: 'quantity',
            cell: ({ row }) => {
                const qty = Number(row.original.quantity) ?? 0;
                const unit = row.original.unitOfWeightMeasurement
                    ? row.original.unitOfWeightMeasurement.toLowerCase()
                    : '';
                return `${qty.toLocaleString('es-MX')} ${unit}`;
            }
        },
        {
            header: 'Precio',
            id: 'final_price',
            accessorFn: (row) => row.final_price ?? row.finalPrice ?? 0,
            cell: ({ getValue }) => formatMoney(parseMoney(getValue())),
        },
        {
            header: 'Costo unitario',
            accessorKey: 'unitPrice',
            cell: ({ row }) => formatMoney(parseMoney(row.original.unitPrice)),
        },
        { header: 'Ubicación', accessorKey: 'locationName' },
        {
            header: 'Folios',
            id: 'folios',
            cell: ({ row }) => {
                const ranges = folioMap[row.original.id];
                if (ranges === undefined) return 'Cargando…';
                if (!ranges.length) return 'Sin folios';
                return formatFoliosForCell(ranges);
            },
        },
        {
            header: 'Evidencia',
            id: 'evidence',
            cell: ({ row }) => evidenceBadge(evidenceMap[row.original.id]),
        },
        {
            header: 'Fecha Entrada', accessorKey: 'acquisitionDate',
            cell: ({ row }) => {
                const v = row.original.acquisitionDate;
                return displayDate(v);
            }
        },
        {
            header: 'Fecha Salida', accessorKey: 'outDate',
            cell: ({ row }) => displayOutDate(row.original.outDate)
        },
        {
            header: 'Estado',
            accessorKey: 'statusName',
            cell: ({ row }) => statusBadge(row.original.statusName || row.original.stage)
        },
        {
            header: 'Etapa',
            accessorKey: 'stage',
            cell: ({ row }) => stageBadge(row.original.stage)
        },
    ];

    const handleUndo = async (itemId) => {
        try {
            const mv = await getLastReversibleMovement(itemId);
            if (!mv) {
                toast.info('No hay un movimiento reversible para este producto.');
                return;
            }

            const ok = await confirmToast(
                `Deshacer movimiento #${mv.id} (${mv.movement_type}). Cantidad: ${mv.quantity}. ¿Continuar?`,
                { okText: 'Sí, deshacer', cancelText: 'Cancelar' }
            );
            if (!ok) return;

            await toast.promise(
                () => undoMovement(mv.id),
                {
                    pending: 'Deshaciendo movimiento…',
                    success: 'Movimiento revertido.',
                    error: {
                        render({ data }) {
                            return data?.message || 'No se pudo deshacer.';
                        }
                    }
                }
            );

            reloadInventory();
            if (showDetails && selectedId === itemId) {
                setShowDetails(false);
                setTimeout(() => setShowDetails(true), 0);
            }
        } catch (e) {
            console.error(e);
            toast.error(e?.message || 'Ocurrió un error al deshacer.');
        }
    };

    const table = useReactTable({
        data,
        columns,
        onSortingChange: setSorting,
        onColumnVisibilityChange: setColumnVisibility,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        state: { pagination, sorting, columnVisibility },
        onPaginationChange: setPagination,
        initialState: { pagination: { pageIndex: 0, pageSize: 10 } },
    });

    const reloadInventory = () => {
        setLoading(true);

        // si hideZero está activo, forzamos min > 0
        const effectiveMin =
            hideZero
                ? (qtyMin !== '' ? Math.max(Number(qtyMin || 0), 0.000001) : 0.000001)
                : (qtyMin !== '' ? Number(qtyMin) : null);

        getInventory(
            selectedCategories,
            selectedLocations,
            selectedStatuses,
            { min: effectiveMin, max: qtyMax !== '' ? Number(qtyMax) : null },
            selectedStages,
            searchText,
            { field: dateField, from: dateFrom || null, to: dateTo || null }
        )
            .then(list => { setData(Array.isArray(list) ? list : []); })
            .finally(() => setTimeout(() => setLoading(false), 200));
    };

    const isAll = (arr, full) => Array.isArray(arr) && Array.isArray(full) && arr.length === full.length;
    const hasQtyFilter = (qtyMin !== '' || qtyMax !== '');
    const hasTextFilter = (searchText && searchText.trim() !== '');
    const hasStageFilter = !isAll(selectedStages, stages.map(s => s.id));
    const hasCatFilter = !isAll(selectedCategories, categories.map(c => c.id));
    const hasLocFilter = !isAll(selectedLocations, locations.map(l => l.id));
    const hasStatusFilter = !isAll(selectedStatuses, statuses.map(s => s.id));
    const hideZeros = hideZero;

    const hasDateFilter = (dateFrom !== '' || dateTo !== '');

    const hasActiveFilters =
        hasQtyFilter || hasTextFilter || hasStageFilter || hasCatFilter || hasLocFilter || hasStatusFilter || hideZeros || hasDateFilter;

    const resetFilters = () => {
        setSearchInput('');
        setSearchText('');
        setQtyMin('');
        setQtyMax('');
        setSelectedStages(stages.map(s => s.id));
        setSelectedCategories(categories.map(c => c.id));
        setSelectedLocations(locations.map(l => l.id));
        setSelectedStatuses(statuses.map(s => s.id));
        setDateField('acquisitionDate');
        setDateFrom('');
        setDateTo('');
        setHideZero(false);
    };

    const totalRows = data.length;
    const pageIndex = table.getState().pagination.pageIndex;
    const pageSize = table.getState().pagination.pageSize;
    const pageCount = table.getPageCount();
    const currentPage = pageCount === 0 ? 0 : pageIndex + 1;
    const visiblePages = getVisiblePages(currentPage, pageCount);

    const openAddModal = () => setShowAdd(true);

    const openEditFromDetails = (itemId) => {
        setSelectedId(itemId);
        setShowDetails(false);
        setShowEdit(true);
    };

    useEffect(() => {
        const t = (searchInput || '').trim();
        const h = setTimeout(() => setSearchText(t), 350);
        return () => clearTimeout(h);
    }, [searchInput]);

    useEffect(() => {
        setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }, [
        searchText,
        selectedCategories,
        selectedLocations,
        selectedStatuses,
        selectedStages,
        qtyMin,
        qtyMax,
        hideZero,
        dateField,
        dateFrom,
        dateTo
    ]);

    useEffect(() => {
        const asArray = (x) => {
            if (Array.isArray(x)) return x;
            if (x && Array.isArray(x.data)) return x.data;
            if (x && Array.isArray(x.result)) return x.result;
            if (x && Array.isArray(x.items)) return x.items;
            return [];
        };

        getCategories()
            .then((cList) => {
                const cats = asArray(cList);
                setCategories(cats);
                setSelectedCategories(cats.map((c) => c.id));
            })
            .finally(() => {
                getLocations()
                    .then((lList) => {
                        const locs = asArray(lList);
                        setLocations(locs);
                        setSelectedLocations(locs.map((l) => l.id));
                    })
                    .finally(() => {
                        getStatuses().then((sList) => {
                            const stats = asArray(sList);
                            setStatuses(stats);
                            setSelectedStatuses(stats.map((s) => s.id));
                        });
                    });
            });
    }, []);

    useEffect(() => {
        const ids = table.getRowModel().rows.map((r) => r.original.id);
        const missing = ids.filter((id) => folioMap[id] === undefined);
        if (!missing.length) return;

        Promise.all(
            missing.map((id) =>
                getItemFolioRanges(id)
                    .then((ranges) => [id, Array.isArray(ranges) ? ranges : []])
                    .catch(() => [id, []])
            )
        ).then((entries) => {
            setFolioMap((prev) => {
                const next = { ...prev };
                for (const [id, ranges] of entries) next[id] = ranges;
                return next;
            });
        });
    }, [data, pagination, sorting, searchText, folioMap]);

    useEffect(() => {
        reloadInventory();
    }, [
        selectedCategories, selectedLocations, selectedStatuses,
        selectedStages, qtyMin, qtyMax, searchText, hideZero,
        dateField, dateFrom, dateTo
    ]);

    useEffect(() => {
        const ids = data.map((x) => x.id);
        if (!ids.length) return;

        getEvidenceSummaryByItems(ids).then((rows) => {
            const next = {};
            rows.forEach((r) => {
                next[r.itemId] = r;
            });
            setEvidenceMap(next);
        });
    }, [data]);


    return (
        <>
            {/* FILTROS */}
            <Offcanvas show={show} onHide={handleClose} placement="start">
                <Offcanvas.Header closeButton>
                    <Offcanvas.Title>Filtros <i className="bi bi-filter"></i></Offcanvas.Title>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    {/* CATEGORÍAS */}
                    <Row className='mt-1'>
                        <Col xl={12}>
                            <h6>Categorías</h6>
                            <ListGroup>
                                {categories.map((checkbox) => (
                                    <ListGroup.Item key={checkbox.id}>
                                        <Form.Check
                                            type="checkbox"
                                            id={`checkbox-${checkbox.id}`}
                                            label={checkbox.name}
                                            checked={selectedCategories.includes(checkbox.id)}
                                            onChange={() => handleCheckboxChangeCategories(checkbox.id)}
                                        />
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </Col>
                    </Row>

                    {/* UBICACIÓN */}
                    <Row className='mt-4'>
                        <Col xl={12}>
                            <h6>Ubicaciónes</h6>
                            <ListGroup>
                                {locations.map((checkbox) => (
                                    <ListGroup.Item key={checkbox.id}>
                                        <Form.Check
                                            type="checkbox"
                                            id={`checkbox-${checkbox.id}`}
                                            label={checkbox.name}
                                            checked={selectedLocations.includes(checkbox.id)}
                                            onChange={() => handleCheckboxChangeLocations(checkbox.id)}
                                        />
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </Col>
                    </Row>

                    {/* ESTATUS */}
                    <Row className='mt-4'>
                        <Col xl={12}>
                            <h6>Estados</h6>
                            <ListGroup>
                                {statuses.map((checkbox) => (
                                    <ListGroup.Item key={checkbox.id}>
                                        <Form.Check
                                            type="checkbox"
                                            id={`checkbox-${checkbox.id}`}
                                            label={checkbox.name}
                                            checked={selectedStatuses.includes(checkbox.id)}
                                            onChange={() => handleCheckboxChangeStatuses(checkbox.id)}
                                        />
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </Col>
                    </Row>

                    {/* ETAPAS */}
                    <Row className='mt-4'>
                        <Col xl={12}>
                            <h6>Etapas</h6>
                            <ListGroup>
                                {stages.map(s => (
                                    <ListGroup.Item key={s.id}>
                                        <Form.Check
                                            type="checkbox"
                                            id={`stage-${s.id}`}
                                            label={s.name}
                                            checked={selectedStages.includes(s.id)}
                                            onChange={() => toggleStage(s.id)}
                                        />
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </Col>
                    </Row>

                    {/* CANTIDAD */}
                    <Row className='mt-4'>
                        <Col xl={12}>
                            <h6>Cantidad</h6>
                            <div className="d-flex gap-2">
                                <Form.Control type="number" placeholder="Mín"
                                    value={qtyMin} onChange={e => setQtyMin(e.target.value)} />
                                <Form.Control type="number" placeholder="Máx"
                                    value={qtyMax} onChange={e => setQtyMax(e.target.value)} />
                            </div>
                        </Col>
                    </Row>

                    {/* FECHAS */}
                    <Row className='mt-4'>
                        <Col xl={12}>
                            <h6>Fechas</h6>

                            <Form.Select
                                className="mb-2"
                                value={dateField}
                                onChange={(e) => setDateField(e.target.value)}
                            >
                                <option value="acquisitionDate">Fecha Entrada</option>
                                <option value="outDate">Fecha Salida</option>
                            </Form.Select>

                            <div className="d-flex gap-2">
                                <Form.Control
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                />
                                <Form.Control
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                />
                            </div>
                        </Col>
                    </Row>
                </Offcanvas.Body>
            </Offcanvas>

            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-3">
                <h2 className="mb-0">Inventario</h2>
                <Button variant="primary" onClick={openAddModal}>
                    Agregar
                </Button>
            </div>

            {/* Barra principal de filtros (como Consumibles) */}
            <Row className="g-3 align-items-end mb-3">
                {/* Etapa */}
                <Col md={3}>
                    <Form.Label>Etapa</Form.Label>
                    <Form.Select value={stageFilter} onChange={handleStageFilter}>
                        <option value="all">Todas</option>
                        {stages.map((s) => (
                            <option key={s.id} value={s.id}>
                                {s.name}
                            </option>
                        ))}
                    </Form.Select>
                </Col>

                {/* Buscar */}
                <Col md={3}>
                    <Form.Label>Buscar</Form.Label>
                    <Form.Control
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        placeholder="Buscar por nombre, categoría, folio..."
                    />
                </Col>

                {/* Por página */}
                <Col md={3}>
                    <Form.Label>Por página</Form.Label>
                    <Form.Select
                        value={pageSize}
                        onChange={(e) => table.setPageSize(Number(e.target.value))}
                    >
                        {[10, 20, 30, 50].map((n) => (
                            <option key={n} value={n}>{n}</option>
                        ))}
                    </Form.Select>
                </Col>
            </Row>

            {/* Barra secundaria */}
            <Row className="g-3 align-items-center mb-3">
                <Col md={4} className="d-flex justify-content-center">
                    <div className="d-flex gap-2 align-items-center">
                        <Button variant="outline-secondary" size="sm" onClick={handleShow}>
                            Filtros <i className="bi bi-filter"></i>
                        </Button>

                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={resetFilters}
                            style={{ visibility: hasActiveFilters ? 'visible' : 'hidden' }}
                        >
                            Limpiar filtros
                        </Button>
                    </div>
                </Col>

                <Col md={4} className="d-flex justify-content-center">
                    <Form.Check
                        type="checkbox"
                        id="toggle-hide-zero"
                        label="Ocultar cantidad = 0"
                        checked={hideZero}
                        onChange={(e) => setHideZero(e.target.checked)}
                    />
                </Col>

                <Col md={4} className="d-flex justify-content-center">
                    <Badge bg="secondary">
                        Objetos desplegados: {totalRows}
                    </Badge>
                </Col>
            </Row>

            {/* Tabla Principal */}
            <div className="table-rounded-wrapper">
                <div className="table-responsive">
                    <Table bordered hover className="mb-0 align-middle">
                        <thead>
                            {
                                table.getHeaderGroups().map(headerGroup => (
                                    <tr key={headerGroup.id} >
                                        {
                                            headerGroup.headers.map(header => (
                                                <th className='text-center'
                                                    key={header.id}
                                                    onClick={header.column.getToggleSortingHandler()}
                                                >
                                                    {header.column.columnDef.header}
                                                    {
                                                        header.column.getIsSorted()
                                                            ? (header.column.getIsSorted() === "asc"
                                                                ? <i className="bi bi-arrow-up-short text-white"></i>
                                                                : <i className="bi bi-arrow-down-short text-white"></i>)
                                                            : null
                                                    }
                                                </th>
                                            ))
                                        }
                                    </tr>
                                ))
                            }
                        </thead>
                        <tbody>
                            {
                                table.getRowModel().rows.map((row) => (
                                    <tr key={row.id}>
                                        {
                                            row.getVisibleCells().map((cell) => (
                                                <td className='text-center' key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                                            ))
                                        }
                                    </tr>
                                ))
                            }
                        </tbody>
                    </Table>
                </div>
            </div>

            {/* Footer / paginación */}
            <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                <div className="text-muted">
                    Página {currentPage} de {pageCount} ({totalRows} registros)
                </div>

                <div className="d-flex gap-2 align-items-center flex-wrap">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => table.setPageIndex(0)}
                        disabled={!table.getCanPreviousPage()}
                    >
                        «
                    </Button>

                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        Anterior
                    </Button>

                    {visiblePages.length > 0 && visiblePages[0] > 1 && (
                        <>
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => table.setPageIndex(0)}
                            >
                                1
                            </Button>
                            {visiblePages[0] > 2 && <span className="px-1 text-muted">…</span>}
                        </>
                    )}

                    {visiblePages.map((pageNumber) => (
                        <Button
                            key={pageNumber}
                            size="sm"
                            variant={pageNumber === currentPage ? 'secondary' : 'outline-secondary'}
                            onClick={() => table.setPageIndex(pageNumber - 1)}
                        >
                            {pageNumber}
                        </Button>
                    ))}

                    {visiblePages.length > 0 && visiblePages[visiblePages.length - 1] < pageCount && (
                        <>
                            {visiblePages[visiblePages.length - 1] < pageCount - 1 && (
                                <span className="px-1 text-muted">…</span>
                            )}
                            <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => table.setPageIndex(pageCount - 1)}
                            >
                                {pageCount}
                            </Button>
                        </>
                    )}

                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        Siguiente
                    </Button>

                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => table.setPageIndex(pageCount - 1)}
                        disabled={!table.getCanNextPage()}
                    >
                        »
                    </Button>
                </div>
            </div>

            {/* Modales */}
            <ItemDetailsModal
                refreshToken={detailsRefresh}
                show={showDetails}
                handleClose={() => setShowDetails(false)}
                handleOpenModalLogs={handleOpenModalLogs}
                id={selectedId}
                onOpenProcess={openProcessFromDetails}
                readOnly={detailsReadOnly}
                onOpenOutput={() => openOutputFromDetails(selectedId)}
                onOpenEdit={openEditFromDetails}
                onOpenEvidence={openEvidenceFromDetails}
            />

            <AddItemModal
                show={showAdd}
                onClose={() => setShowAdd(false)}
                onSuccess={(createdId) => {
                    setShowAdd(false);
                    reloadInventory();

                    if (createdId) {
                        setSelectedId(createdId);
                        setDetailsRefresh((n) => n + 1);
                        setShowDetails(true);
                    }
                }}
            />

            <EditItemModal
                show={showEdit}
                id={selectedId}
                onClose={() => {
                    setShowEdit(false);
                    setShowDetails(true);
                }}
                onSuccess={() => {
                    setShowEdit(false);
                    reloadInventory();
                    setDetailsRefresh((n) => n + 1);
                    setShowDetails(true);
                }}
            />

            <ItemLogsModal
                show={showLogs}
                handleClose={() => setShowLogs(false)}
                id={selectedId}
                handleBack={() => {
                    setShowLogs(false);
                    setShowDetails(true)
                }}
            />

            <ProcessModal
                show={showProcess}
                onClose={() => setShowProcess(false)}
                itemId={processMeta.itemId}
                currentStage={processMeta.stage}
                locations={locations}
                forceProcessingForm={processMeta.forceProcessingForm}
                onSuccess={async (toItemId) => {
                    setShowProcess(false);
                    reloadInventory();
                    if (toItemId) {
                        setSelectedId(toItemId);
                        setDetailsReadOnly(false);
                        setShowDetails(true);
                    } else {
                        setShowDetails(true);
                    }
                }}
            />

            <OutputModal
                show={showOutput}
                onClose={() => {
                    setShowOutput(false);
                    setShowDetails(true);
                }}
                itemId={selectedId}
                onSuccess={() => {
                    setShowOutput(false);
                    reloadInventory();
                    setDetailsRefresh((n) => n + 1);
                    setShowDetails(true);
                }}
            />

            <UploadFilesModal
                show={showEvidence}
                handleClose={() => {
                    setShowEvidence(false);
                    setShowDetails(true);
                }}
                itemId={selectedId}
                itemName={selectedItemName}
                onSuccess={() => {
                    reloadInventory();
                    setDetailsRefresh((n) => n + 1);
                }}
            />
        </>
    )
};

export default Inventory;